import { Field, FieldError, FieldLabel, Input } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { profileAddressFormSchema } from "../schemas";

interface UpdateAddressFormProps {
  handleSubmit: (data: z.infer<typeof profileAddressFormSchema>) => void;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  isUpdatingAddress: boolean;
  setIsEditingAddress: (isEditing: boolean) => void;
}

export const UpdateAddressForm = ({
  handleSubmit,
  address,
  isUpdatingAddress,
  setIsEditingAddress,
}: UpdateAddressFormProps) => {
  const form = useForm<z.infer<typeof profileAddressFormSchema>>({
    resolver: zodResolver(profileAddressFormSchema),
    defaultValues: {
      addressLine_1: address?.line1 || "",
      addressLine_2: address?.line2 || "",
      city: address?.city || "",
      state: address?.state || "",
      postalCode: address?.zip || "",
    },
  });
  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-2 px-3 py-2"
    >
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="addressLine_1"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="addressLine_1">
                  <BodyText size="sm" bold>
                    Address Line 1
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="addressLine_1" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="addressLine_2"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="addressLine_2">
                  <BodyText size="sm" bold>
                    Address Line 2 (Optional)
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="addressLine_2" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="city"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="city">
                  <BodyText size="sm" bold>
                    City
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="city" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="state"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="state">
                  <BodyText size="sm" bold>
                    State
                  </BodyText>
                </FieldLabel>
                <Input
                  {...field}
                  id="state"
                  maxLength={2}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="postalCode"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="postalCode">
                  <BodyText size="sm" bold>
                    Postal Code
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="postalCode" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          {/* Empty div to maintain layout balance */}
        </div>
      </div>
      <SubmitCancelButtons
        isSaving={isUpdatingAddress}
        onCancel={() => {
          setIsEditingAddress(false);
        }}
      />
    </form>
  );
};
